import { cva, type VariantProps } from 'class-variance-authority'

/**
 * Promo Block CVA Configuration — overlay "banner band" design (TSS-535)
 *
 * Layout: the FULL image is shown without cropping (object-contain) inside a
 * fixed 4:3 card — same image fits on mobile and desktop. An orange band
 * (bg-main-color) holding the title / description / button in black text is
 * overlaid at the BOTTOM and revealed ON HOVER (always visible on touch, which
 * has no hover). Cards are rendered two-per-row by PromoBlockRow.
 *
 * Design tokens from tailwind.config.js:
 * - main-color (#F7B01A) - orange brand accent → band background
 * - text-color (#1c1b21) - black text on the orange band
 * - cards-bg (#F5F5F5) - letterbox / skeleton fill
 * - border-color (#e5e5e5) - card border
 * - font-main-font (Poppins) - all text
 */

/**
 * Promo block card — the outer <article>. Positioned so the band can be
 * absolutely overlaid; a fixed 4:3 aspect ratio (same on all breakpoints) keeps
 * paired cards aligned and shows the same framing on mobile and desktop.
 */
export const promoBlockSectionVariants = cva(
  'group relative w-full overflow-hidden border border-border-color bg-cards-bg shadow-sm ' +
  'aspect-[4/3]'
)

export type PromoBlockSectionVariants = VariantProps<typeof promoBlockSectionVariants>

/**
 * Promo block inner wrapper — fills the card.
 */
export const promoBlockContainerVariants = cva('absolute inset-0')

export type PromoBlockContainerVariants = VariantProps<typeof promoBlockContainerVariants>

/**
 * Image container — fills the card behind the band.
 */
export const promoBlockImageSectionVariants = cva('absolute inset-0 overflow-hidden')

export type PromoBlockImageSectionVariants = VariantProps<typeof promoBlockImageSectionVariants>

/**
 * Image link wrapper — covers the whole card so the image is clickable.
 */
export const promoBlockImageLinkVariants = cva('block h-full w-full')

export type PromoBlockImageLinkVariants = VariantProps<typeof promoBlockImageLinkVariants>

/**
 * Image — object-contain so the WHOLE image is shown (never cropped); the card's
 * bg-cards-bg fills any letterbox space when the source aspect isn't 4:3.
 */
export const promoBlockImageVariants = cva('h-full w-full object-contain')

export type PromoBlockImageVariants = VariantProps<typeof promoBlockImageVariants>

/**
 * Content section — the ORANGE BAND overlaid at the bottom of the card.
 * Hidden by default and revealed on hover/focus (md+); always visible on
 * mobile/touch, which has no hover.
 */
export const promoBlockContentSectionVariants = cva([
  'absolute inset-x-0 bottom-0 z-10',
  'bg-main-color',
  'px-4 py-3 md:px-6 md:py-4',
  'opacity-100 translate-y-0',
  'md:opacity-0 md:translate-y-2 md:transition-all md:duration-300 md:ease-out',
  'md:group-hover:opacity-100 md:group-hover:translate-y-0',
  'md:group-focus-within:opacity-100 md:group-focus-within:translate-y-0',
])

export type PromoBlockContentSectionVariants = VariantProps<typeof promoBlockContentSectionVariants>

/**
 * Content inner wrapper — text block inside the band.
 */
export const promoBlockContentInnerVariants = cva('text-left')

export type PromoBlockContentInnerVariants = VariantProps<typeof promoBlockContentInnerVariants>

/**
 * Title — black, on the orange band.
 */
export const promoBlockTitleVariants = cva(
  'text-base md:text-xl font-bold uppercase tracking-wide leading-snug text-text-color font-main-font'
)

export type PromoBlockTitleVariants = VariantProps<typeof promoBlockTitleVariants>

/**
 * Description — black, on the orange band. Clamped so tall copy never blows out
 * the band height across a paired row.
 */
export const promoBlockDescriptionVariants = cva(
  'mt-1 text-xs md:text-sm leading-relaxed text-text-color/90 font-main-font line-clamp-2'
)

export type PromoBlockDescriptionVariants = VariantProps<typeof promoBlockDescriptionVariants>

/**
 * Skeleton — the orange band region while loading.
 */
export const promoBlockSkeletonContentVariants = cva([
  'absolute inset-x-0 bottom-0 z-10',
  'bg-main-color',
  'px-4 py-3 md:px-6 md:py-4',
])

export type PromoBlockSkeletonContentVariants = VariantProps<typeof promoBlockSkeletonContentVariants>

/**
 * Skeleton — inner content of the band.
 */
export const promoBlockSkeletonInnerVariants = cva('space-y-2')

export type PromoBlockSkeletonInnerVariants = VariantProps<typeof promoBlockSkeletonInnerVariants>
